import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ClientSidebar from "../components/ClientSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { updateProfile } from "@/lib/account";
import { ApiError } from "@/lib/api";

export default function PerfilEditarPage() {
  const { t } = useTranslation("profile");
  const { user, refreshMe } = useAuth();

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form from the signed-in user, and re-seed if it refreshes underneath us.
  useEffect(() => {
    if (user) {
      setFullName(user.fullName);
      setPhone(user.phone ?? "");
    }
  }, [user]);

  const dirty = user ? fullName !== user.fullName || phone !== (user.phone ?? "") : false;
  const canSave = dirty && fullName.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await updateProfile({ fullName: fullName.trim(), phone: phone.trim() || undefined });
      await refreshMe();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("profile.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20 pb-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <ClientSidebar active="overview" />
            <div className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h1 className="text-xl font-bold text-charcoal">{t("profile.editProfile")}</h1>
                  {saved && (
                    <span className="text-xs font-medium text-ocean bg-ocean/10 px-3 py-1 rounded-full">{t("profile.saved")}</span>
                  )}
                </div>

                {error && (
                  <div className="bg-coral/10 border border-coral/30 text-coral text-sm px-4 py-3 rounded-xl mb-6">{error}</div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="profile-fullname" className="block text-sm font-medium text-charcoal mb-1.5">{t("profile.fullName")}</label>
                    <input
                      id="profile-fullname"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean"
                    />
                  </div>
                  <div>
                    <label htmlFor="profile-email" className="block text-sm font-medium text-charcoal mb-1.5">{t("profile.email")}</label>
                    <input
                      id="profile-email"
                      type="email"
                      value={user?.email ?? ""}
                      readOnly
                      disabled
                      className="w-full px-4 py-2.5 border border-gray-200 bg-gray-50 text-gray-500 rounded-lg text-sm focus:outline-none"
                    />
                    <p className="text-xs text-gray-400 mt-1">{t("profile.emailReadOnly")}</p>
                  </div>
                  <div>
                    <label htmlFor="profile-phone" className="block text-sm font-medium text-charcoal mb-1.5">{t("profile.phone")}</label>
                    <input
                      id="profile-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canSave}
                    className="bg-ocean text-white text-sm font-medium px-6 py-2.5 rounded-full hover:bg-ocean/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                    {saving ? t("profile.saving") : t("profile.save")}
                  </button>
                  <Link to="/perfil" className="border border-gray-200 text-charcoal text-sm font-medium px-6 py-2.5 rounded-full hover:bg-gray-50 transition-colors">
                    {t("profile.cancel")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
